import { debounce, get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Icd, PatientService } from '../../containers/my-central/patientService';
import { toast } from '../../helpers/toast';
import { useLoading } from '../../helpers/useLoading';
import AutoComplete from './AutoComplete';
import { FormControl } from './FormControl';
import { FormErrorMessage } from './FormErrorMessage';
import { FormLabel } from './FormLabel';
import { logger } from '../../helpers/logger';

interface SearchIcdProps {
  label?: string;
  hasError?: boolean;
  errorMessage?: string;
  icd: Icd | null;
  medicineId?: number | null;
  setIcd(icd: Icd | null): void;
  clearAfterSelect?: boolean;
  menuMaxHeight?: string;
  placeholder?: string;
  onBlur?(event: React.FocusEvent<HTMLElement>): void;
  onFocus?(): void;
}

function SearchIcd(props: SearchIcdProps) {
  const {
    hasError,
    label,
    errorMessage,
    icd,
    medicineId,
    setIcd,
    clearAfterSelect,
    menuMaxHeight,
    placeholder,
    onFocus,
  } = props;

  const [icdOptions, setIcdOptions] = useState<Icd[]>([]);
  const [value, setValue] = useState<string | null>('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, runWithLoading] = useLoading();

  useEffect(() => {
    if (!icd) {
      setValue('');
      return;
    }
    setValue(`${icd.subcategory} - ${icd.description}`);
  }, [icd]);

  const findIcd = useMemo(
    () =>
      debounce(async (icd: string) => {
        if (icd === '') return;

        try {
          runWithLoading(async () => {
            if (medicineId) {
              const icdData = await PatientService.getIcdByMedicine(icd, medicineId);
              setIcdOptions(icdData);
              if (!isMenuOpen) {
                setIsMenuOpen(true);
              }
              return;
            }
            const icdData = await PatientService.getIcd(icd);
            setIcdOptions(icdData);
            if (!isMenuOpen) {
              setIsMenuOpen(true);
            }
          });
        } catch (e) {
          logger.error(e);
          toast.error('Não foi possível buscar os diagnósticos, tente novamente.');
        }
      }, 400),
    [medicineId, runWithLoading, isMenuOpen]
  );

  const onInputIcd = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventTargetValue = get(event, 'target.value', null);

    if (eventTargetValue === '') {
      setValue('');
      setIcd(null);
      setIsMenuOpen(false);
      return;
    }

    findIcd(eventTargetValue || '');
    setValue(eventTargetValue);
  };

  const onSelect = (icd: Icd) => {
    setIcd(icd);
    setValue(`${icd.subcategory} - ${icd.description}`);
    setIsMenuOpen(false);

    if (clearAfterSelect) {
      setValue(``);
    }
  };

  useEffect(() => {
    if (!value) {
      setIsMenuOpen(false);
      return;
    }
  }, [value]);

  const onBlur = (event: React.FocusEvent<HTMLElement>) => {
    setIsMenuOpen(false);
    if (props.onBlur) props.onBlur(event);
  };

  return (
    <FormControl isInvalid={hasError || false}>
      {label && <FormLabel>{label}</FormLabel>}
      <AutoComplete
        onInputChange={onInputIcd}
        inputValue={value || ''}
        options={icdOptions}
        onSelect={onSelect}
        isMenuOpen={isMenuOpen}
        onBlur={onBlur}
        onFocus={onFocus}
        onCloseMenu={() => setIsMenuOpen(false)}
        noOptionsText={'Nenhum diagnóstico encontrado'}
        placeholder={placeholder || 'Informe o diagnóstico'}
        getOptionLabel={(option: Icd) => `${option.subcategory} - ${option.description}`}
        menuMaxHeigh={menuMaxHeight || '100px'}
        onClearInput={() => {
          setValue('');
          findIcd('');
          setIcd(null);
        }}
        isLoading={loading}
      />

      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
}

export default React.memo(SearchIcd);
